import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { AlertCircle, ChevronDown, ChevronUp, Database } from 'lucide-react'
import type { IngestionRun, IngestionRunStatus } from '../../../../types/ingestion'

const RUN_STATUS_COLOURS: Record<IngestionRunStatus, string> = {
  pending: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
  extracting: 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400',
  parsing: 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400',
  verifying: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400',
  completed_with_issues: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400',
}

interface EmptyStateProps {
  t: TFunction<'cases'>
}

export function EmptyState({ t }: EmptyStateProps): ReactElement {
  return (
    <div className="py-10 text-center">
      <Database className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
      <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
        {t('detail.processing.noRuns')}
      </p>
    </div>
  )
}

export function ErrorState({ message }: { message: string }): ReactElement {
  return (
    <div className="flex items-center gap-2 py-6 text-red-600" role="alert">
      <AlertCircle className="h-4 w-4 shrink-0" />
      <span className="text-sm">{message}</span>
    </div>
  )
}

export function LoadingState({ t }: { t: TFunction<'cases'> }): ReactElement {
  return (
    <div
      className="py-8 text-center text-sm text-slate-500 dark:text-slate-400"
      role="status"
      aria-busy="true"
    >
      {t('detail.processing.processing')}
    </div>
  )
}

interface TableHeaderProps {
  t: TFunction<'cases'>
}

export function TableHeader({ t }: TableHeaderProps): ReactElement {
  const headerClass = 'py-2 pr-4 text-left font-medium text-slate-600 dark:text-slate-400'
  const rightClass = 'py-2 pr-4 text-right font-medium text-slate-600 dark:text-slate-400'
  return (
    <thead>
      <tr className="border-b border-slate-200 dark:border-slate-700">
        <th className={headerClass}>{t('detail.processing.triggered')}</th>
        <th className={headerClass}>{t('detail.processing.evidenceNumber')}</th>
        <th className={headerClass}>{t('detail.processing.status')}</th>
        <th className={rightClass}>{t('detail.processing.totalRows')}</th>
        <th className={rightClass}>{t('detail.processing.parsedRows')}</th>
        <th className="py-2 text-right font-medium text-slate-600 dark:text-slate-400">
          {t('detail.processing.skippedRows')}
        </th>
      </tr>
    </thead>
  )
}

interface StatusCellProps {
  run: IngestionRun
  t: TFunction<'cases'>
}

function StatusCell({ run, t }: StatusCellProps): ReactElement {
  return (
    <td className="py-2 pr-4">
      <span
        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${RUN_STATUS_COLOURS[run.status]}`}
      >
        {run.status.replaceAll('_', ' ')}
      </span>
      {run.isVerified && (
        <span className="ml-1.5 inline-flex rounded-full bg-lime-100 px-2 py-0.5 text-xs font-medium text-lime-700 dark:bg-lime-950/50 dark:text-lime-400">
          {t('detail.processing.verified')}
        </span>
      )}
      {run.isCorrupt && (
        <span className="ml-1.5 inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950/50 dark:text-red-400">
          {t('detail.processing.corrupt')}
        </span>
      )}
    </td>
  )
}

interface RunRowProps {
  run: IngestionRun
  isSelected: boolean
  onSelect: (id: string) => void
  t: TFunction<'cases'>
}

export function RunRow({ run, isSelected, onSelect, t }: RunRowProps): ReactElement {
  const handleKey = (ev: React.KeyboardEvent): void => {
    if (ev.key === 'Enter' || ev.key === ' ') onSelect(run.id)
  }
  return (
    <tr
      className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/50"
      onClick={() => {
        onSelect(run.id)
      }}
      tabIndex={0}
      role="button"
      aria-expanded={isSelected}
      onKeyDown={handleKey}
    >
      <td className="py-2 pr-4 text-slate-500 dark:text-slate-400">
        {new Date(run.triggeredAt).toLocaleString()}
      </td>
      <td className="py-2 pr-4 font-mono text-xs text-slate-900 dark:text-slate-100">
        {run.evidenceNumberId}
      </td>
      <StatusCell run={run} t={t} />
      <td className="py-2 pr-4 text-right text-slate-700 dark:text-slate-300">
        {run.totalExpectedRows?.toLocaleString() ?? '—'}
      </td>
      <td className="py-2 pr-4 text-right text-slate-700 dark:text-slate-300">
        {run.totalParsedRows.toLocaleString()}
      </td>
      <td className="py-2 text-right">
        <div className="flex items-center justify-end gap-2">
          <span className="text-slate-700 dark:text-slate-300">
            {run.totalSkippedRows.toLocaleString()}
          </span>
          {isSelected ? (
            <ChevronUp className="h-3.5 w-3.5 text-slate-400" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
          )}
        </div>
      </td>
    </tr>
  )
}
